import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import ReactMarkdown from "react-markdown";
import { ArrowClockwise, ChatCircleDots, Info } from "@phosphor-icons/react";

/**
 * Job-coach view of a coachee's STARR interview practice session.
 * Read-only transcript + a single big "Reset" button that wipes the session
 * so the coachee can practice again.
 */
export default function InterviewCoachPanel({ coacheeId, coacheeName }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  const fetchSession = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/coachees/${coacheeId}/interview`);
      setSession(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSession(); }, [coacheeId]);

  const handleReset = async () => {
    setResetting(true);
    setError("");
    try {
      await api.post(`/coachees/${coacheeId}/interview/reset`);
      setConfirmOpen(false);
      await fetchSession();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResetting(false);
    }
  };

  const used = session?.user_message_count ?? 0;
  const cap = session?.max_user_messages ?? 60;
  const limitReached = session?.limit_reached === true || used >= cap;
  const messages = session?.messages || [];

  return (
    <div className="space-y-4 max-w-3xl" data-testid="interview-coach-panel">
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3">
          <ChatCircleDots size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">
              STARR sollicitatiegesprek-oefening
            </h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              {coacheeName || "De coachee"} kan deze oefening tot {cap} keer een bericht
              sturen. Daarna stopt de oefening totdat je hieronder op <em>Resetten</em> klikt.
            </p>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs text-zinc-500" data-testid="interview-coach-counter">
                Berichten gebruikt: <strong className="text-zinc-900">{used}</strong> / {cap}
                {limitReached && (
                  <span className="ml-2 inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide bg-[#E53935]/10 text-[#E53935] border border-[#E53935]/30 rounded-sm">
                    Limiet bereikt
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={resetting}
                data-testid="interview-reset-btn"
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
              >
                <ArrowClockwise size={16} /> Oefening resetten
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div
          className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm"
          data-testid="interview-coach-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden...</div>
      ) : messages.length === 0 ? (
        <div className="neuro-card p-6 bg-white" data-testid="interview-coach-empty">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">Nog niet gestart</h4>
              <p className="text-sm text-zinc-600 mt-1">
                {coacheeName || "De coachee"} heeft de oefening nog niet gestart.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="neuro-card p-4 bg-white max-h-[480px] overflow-y-auto space-y-3" data-testid="interview-coach-thread">
          {messages.map((m, i) => (
            <Bubble key={m.ts ? `${m.ts}-${m.role}` : `i-${i}`} role={m.role} content={m.content} />
          ))}
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !resetting && setConfirmOpen(false)}>
          <div
            className="bg-white p-6 max-w-md w-full rounded-sm shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="interview-reset-confirm"
          >
            <h4 className="font-display text-lg font-bold tracking-tight">Oefening resetten?</h4>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              De volledige chatgeschiedenis wordt gewist en de berichten-teller springt terug
              naar 0. {coacheeName || "De coachee"} kan daarna opnieuw oefenen. Deze actie
              kan niet ongedaan worden gemaakt.
            </p>
            <div className="flex gap-2 mt-4 justify-end">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={resetting}
                data-testid="interview-reset-cancel"
                className="px-4 py-2 text-sm font-medium border border-zinc-300 rounded-sm hover:bg-zinc-100 transition-colors"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={resetting}
                data-testid="interview-reset-confirm-btn"
                className="px-4 py-2 text-sm font-semibold uppercase tracking-wide bg-[#E53935] text-white rounded-sm hover:bg-[#C62828] transition-colors disabled:bg-zinc-400"
              >
                {resetting ? "Resetten..." : "Ja, resetten"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={"flex " + (isUser ? "justify-end" : "justify-start")}>
      <div
        className={
          "max-w-[80%] px-3.5 py-2.5 rounded-sm text-sm whitespace-pre-wrap leading-relaxed " +
          (isUser
            ? "bg-zinc-900 text-white"
            : "bg-zinc-100 text-zinc-900 border border-zinc-200")
        }
      >
        {isUser ? content : (
          <div className="prose prose-sm max-w-none prose-zinc prose-headings:font-display prose-headings:tracking-tight prose-p:my-2 prose-li:my-0">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
